import React, { useState, useEffect } from 'react';
import { Film, Popcorn, Users, Download, Sliders, Phone, Mail, ExternalLink } from 'lucide-react';
import { useOffer } from '../context/OfferContext';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Initialize pdfMake with fonts
if (pdfMake.vfs === undefined) {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
}

const FatraOfferPage = () => {
  const { offer, loading, error } = useOffer();
  const [guestCount, setGuestCount] = useState(500);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center">
        <div className="text-cyan-400 text-xl">Načítání...</div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center">
        <div className="text-red-400 text-xl">Nabídka nenalezena</div>
      </div>
    );
  }

  // Výpočet dynamických hodnot
  const basePeople = 500;
  const ratio = guestCount / basePeople;

  // Technika
  const chairs = guestCount;
  const chairsPrice = chairs * 50;
  const technicalTotal = 30000 + 8000 + 15000 + chairsPrice;

  // Personál technika (fixní)
  const technicalPersonnelTotal = (1 * 500 * 8) + (1 * 400 * 8) + (4 * 200 * 6);

  // Catering - přepočet porcí
  const popcornPortions = Math.round(200 * ratio);
  const nachosPortions = Math.round(200 * ratio);
  const hranolkyPortions = Math.round(200 * ratio);
  const zmrzlinaPortions = Math.round(500 * ratio);

  const popcornPrice = popcornPortions * 144;
  const nachosPrice = nachosPortions * 144;
  const hranolkyPrice = hranolkyPortions * 70;
  const zmrzlinaPrice = zmrzlinaPortions * 50;
  const cateringFoodTotal = popcornPrice + nachosPrice + hranolkyPrice + zmrzlinaPrice;

  // Nápoje - přepočet
  const radegastPortions = Math.round(200 * ratio);
  const birellPortions = Math.round(200 * ratio);
  const limonadaPortions = Math.round(100 * ratio);
  const vodaPortions = Math.round(100 * ratio);

  const radegastPrice = radegastPortions * 45;
  const birellPrice = birellPortions * 45;
  const limonadaPrice = limonadaPortions * 40;
  const vodaPrice = vodaPortions * 35;
  const beveragesTotal = radegastPrice + birellPrice + limonadaPrice + vodaPrice;

  // Personál catering (fixní)
  const cateringPersonnelTotal = (2 * 350 * 5) + (2 * 350 * 5) + (1 * 200 * 5) + (1 * 200 * 5);

  // Cateringová technika (fixní)
  const cateringEquipmentTotal = 5000 + 5000 + 3500;

  // Celková cena
  const totalPrice = technicalTotal + technicalPersonnelTotal + cateringFoodTotal + 
                     beveragesTotal + cateringPersonnelTotal + cateringEquipmentTotal;
  
  const pricePerPerson = Math.round(totalPrice / guestCount);

  // PDF Export funkce - pouze tabulky
  const exportTablesOnly = () => {
    const docDefinition = {
      content: [
        // Fakturační hlavička
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Jakub Vodárek', style: 'companyName', margin: [0, 0, 0, 5] },
                { text: 'IČO: 74953613', style: 'companyInfo' },
                { text: 'DIČ: CZ8805254557', style: 'companyInfo' },
                { text: 'SNP 1187, Kvítkovice', style: 'companyInfo' },
                { text: '76502 Otrokovice', style: 'companyInfo', margin: [0, 0, 0, 5] },
                { text: 'Tel: 774 750 456', style: 'companyInfo' },
                { text: 'Email: info@vseproakci.cz', style: 'companyInfo' }
              ]
            },
            {
              width: 'auto',
              stack: [
                { text: 'www.vseproakci.cz', style: 'website', alignment: 'right', link: 'https://vseproakci.cz', color: '#0066B3' }
              ]
            }
          ],
          margin: [0, 0, 0, 20]
        },
        
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#cccccc' }], margin: [0, 0, 0, 20] },
        
        { text: 'LETNÍ KINO FATRA A.S.', style: 'header', alignment: 'center' },
        { text: 'Kalkulace akce', style: 'subheader', alignment: 'center', margin: [0, 0, 0, 20] },
        { text: `Počet návštěvníků: ${guestCount} osob`, style: 'info', margin: [0, 0, 0, 10] },
        { text: `Datum: ${offer.eventDate}`, style: 'info', margin: [0, 0, 0, 20] },
        
        // Tabulka 1: Soupis techniky
        { text: 'Soupis techniky', style: 'tableHeader', margin: [0, 10, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeaderCell' },
                { text: 'Počet ks', style: 'tableHeaderCell' },
                { text: 'Cena / ks', style: 'tableHeaderCell' },
                { text: 'Cena celkem', style: 'tableHeaderCell' }
              ],
              ['LED obrazovka 5x3m', '1', '30 000 Kč', '30 000 Kč'],
              ['Konstrukce LED obrazovky', '1', '8 000 Kč', '8 000 Kč'],
              ['Ozvučení – kompletní realizace', '1', '15 000 Kč', '15 000 Kč'],
              ['Židle', chairs.toString(), '50 Kč', `${chairsPrice.toLocaleString('cs-CZ')} Kč`],
              [{ text: 'CELKEM:', bold: true, colSpan: 3 }, {}, {}, { text: `${technicalTotal.toLocaleString('cs-CZ')} Kč`, bold: true }]
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 15]
        },

        // Tabulka 2: Personál technika
        { text: 'Personál - technické zajištění', style: 'tableHeader', margin: [0, 10, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Název pracovní pozice', style: 'tableHeaderCell' },
                { text: 'Počet', style: 'tableHeaderCell' },
                { text: 'Cena/hod', style: 'tableHeaderCell' },
                { text: 'Hodiny', style: 'tableHeaderCell' },
                { text: 'Celkem', style: 'tableHeaderCell' }
              ],
              ['Technik LED obrazovka', '1', '500 Kč', '8', '4 000 Kč'],
              ['Zvukař', '1', '400 Kč', '8', '3 200 Kč'],
              ['Montážní personál', '4', '200 Kč', '6', '4 800 Kč'],
              [{ text: 'CELKEM:', bold: true, colSpan: 4 }, {}, {}, {}, { text: `${technicalPersonnelTotal.toLocaleString('cs-CZ')} Kč`, bold: true }]
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 15]
        },

        // Tabulka 3: Catering jídlo
        { text: 'Catering - jídlo', style: 'tableHeader', margin: [0, 10, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Název', style: 'tableHeaderCell' },
                { text: 'Gramáž', style: 'tableHeaderCell' },
                { text: 'Porce', style: 'tableHeaderCell' },
                { text: 'Cena/porce', style: 'tableHeaderCell' },
                { text: 'Celkem', style: 'tableHeaderCell' }
              ],
              ['Popcorn', '3L', popcornPortions.toString(), '144 Kč', `${popcornPrice.toLocaleString('cs-CZ')} Kč`],
              ['Nachos + 2 dipy', '150g', nachosPortions.toString(), '144 Kč', `${nachosPrice.toLocaleString('cs-CZ')} Kč`],
              ['Hranolky + 2 dipy', '150g', hranolkyPortions.toString(), '70 Kč', `${hranolkyPrice.toLocaleString('cs-CZ')} Kč`],
              ['Točená zmrzlina', '120g', zmrzlinaPortions.toString(), '50 Kč', `${zmrzlinaPrice.toLocaleString('cs-CZ')} Kč`],
              [{ text: 'CELKEM:', bold: true, colSpan: 4 }, {}, {}, {}, { text: `${cateringFoodTotal.toLocaleString('cs-CZ')} Kč`, bold: true }]
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 15]
        },

        // Tabulka 4: Catering nápoje
        { text: 'Catering - nápoje', style: 'tableHeader', margin: [0, 10, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Název', style: 'tableHeaderCell' },
                { text: 'Objem', style: 'tableHeaderCell' },
                { text: 'Porce', style: 'tableHeaderCell' },
                { text: 'Cena/porce', style: 'tableHeaderCell' },
                { text: 'Celkem', style: 'tableHeaderCell' }
              ],
              ['Radegast 10', '500 ml', radegastPortions.toString(), '45 Kč', `${radegastPrice.toLocaleString('cs-CZ')} Kč`],
              ['Birell Pomelo & Grep', '500 ml', birellPortions.toString(), '45 Kč', `${birellPrice.toLocaleString('cs-CZ')} Kč`],
              ['Točená limonáda', '500 ml', limonadaPortions.toString(), '40 Kč', `${limonadaPrice.toLocaleString('cs-CZ')} Kč`],
              ['Voda PET', '500 ml', vodaPortions.toString(), '35 Kč', `${vodaPrice.toLocaleString('cs-CZ')} Kč`],
              [{ text: 'CELKEM:', bold: true, colSpan: 4 }, {}, {}, {}, { text: `${beveragesTotal.toLocaleString('cs-CZ')} Kč`, bold: true }]
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 15]
        },

        // Tabulka 5: Personál catering
        { text: 'Personál - catering', style: 'tableHeader', margin: [0, 10, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Název pracovní pozice', style: 'tableHeaderCell' },
                { text: 'Počet', style: 'tableHeaderCell' },
                { text: 'Cena/hod', style: 'tableHeaderCell' },
                { text: 'Hodiny', style: 'tableHeaderCell' },
                { text: 'Celkem', style: 'tableHeaderCell' }
              ],
              ['Kuchař', '2', '350 Kč', '5', '3 500 Kč'],
              ['Barman', '2', '350 Kč', '5', '3 500 Kč'],
              ['Zmrzlinář', '1', '200 Kč', '5', '1 000 Kč'],
              ['Pomocný personál', '1', '200 Kč', '5', '1 000 Kč'],
              [{ text: 'CELKEM:', bold: true, colSpan: 4 }, {}, {}, {}, { text: `${cateringPersonnelTotal.toLocaleString('cs-CZ')} Kč`, bold: true }]
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 15]
        },

        // Tabulka 6: Cateringová technika
        { text: 'Cateringová technika', style: 'tableHeader', margin: [0, 10, 0, 5] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeaderCell' },
                { text: 'Počet ks', style: 'tableHeaderCell' },
                { text: 'Cena celkem', style: 'tableHeaderCell' }
              ],
              ['Beer Truck XXL', '1', '5 000 Kč'],
              ['Sweet Truck – zmrzlina', '1', '5 000 Kč'],
              ['Cooking stan 6x3m', '1', '3 500 Kč'],
              [{ text: 'CELKEM:', bold: true, colSpan: 2 }, {}, { text: `${cateringEquipmentTotal.toLocaleString('cs-CZ')} Kč`, bold: true }]
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 0, 0, 20]
        },

        // Celková cena
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              [
                { text: 'CELKOVÁ CENA ZA AKCI (bez DPH):', style: 'finalPrice', border: [false, false, false, false] },
                { text: `${totalPrice.toLocaleString('cs-CZ')} Kč`, style: 'finalPriceValue', border: [false, false, false, false] }
              ],
              [
                { text: `Cena na osobu (${guestCount} osob):`, style: 'pricePerPerson', border: [false, false, false, false] },
                { text: `${pricePerPerson} Kč`, style: 'pricePerPersonValue', border: [false, false, false, false] }
              ]
            ]
          },
          margin: [0, 10, 0, 0]
        },
        
        // Poznámka o DPH
        {
          text: 'Ceny jsou uvedeny bez DPH.',
          style: 'vatNote',
          alignment: 'center',
          margin: [0, 10, 0, 0]
        }
      ],
      styles: {
        companyName: { fontSize: 14, bold: true },
        companyInfo: { fontSize: 10, color: '#666666' },
        website: { fontSize: 10, bold: true },
        header: { fontSize: 22, bold: true, margin: [0, 0, 0, 10] },
        subheader: { fontSize: 16, margin: [0, 0, 0, 10] },
        info: { fontSize: 12 },
        tableHeader: { fontSize: 14, bold: true, fillColor: '#eeeeee' },
        tableHeaderCell: { bold: true, fillColor: '#dddddd' },
        finalPrice: { fontSize: 16, bold: true, margin: [0, 10, 0, 5] },
        finalPriceValue: { fontSize: 20, bold: true, alignment: 'right', margin: [0, 10, 0, 5] },
        pricePerPerson: { fontSize: 12, margin: [0, 5, 0, 0] },
        pricePerPersonValue: { fontSize: 14, bold: true, alignment: 'right', margin: [0, 5, 0, 0] },
        vatNote: { fontSize: 10, italics: true, color: '#666666' }
      },
      defaultStyle: { font: 'Roboto' }
    };

    pdfMake.createPdf(docDefinition).download(`Fatra_Letni_Kino_Kalkulace_${guestCount}_osob.pdf`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e27] via-[#0f1535] to-[#0a0e27]">
      {/* Hero Section */}
      <div className="relative h-[600px] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${offer.heroImage})`,
            filter: 'brightness(0.4)'
          }}
        />
        
        {/* Film strip decoration */}
        <div className="absolute top-0 left-0 w-full h-4 bg-gradient-to-r from-transparent via-[#0066B3] to-transparent opacity-50" />
        <div className="absolute bottom-0 left-0 w-full h-4 bg-gradient-to-r from-transparent via-[#0066B3] to-transparent opacity-50" />

        {/* Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-4xl">
            {/* Logo */}
            <img 
              src={offer.clientLogo} 
              alt={offer.clientName}
              className="h-20 mx-auto mb-8"
            />
            
            <div className="flex items-center justify-center gap-4 mb-6">
              <Film className="w-12 h-12 text-[#0066B3]" />
              <h1 className="text-6xl font-bold text-white">
                LETNÍ KINO {offer.clientName.split(' ')[0].toUpperCase()}
              </h1>
              <Film className="w-12 h-12 text-[#0066B3]" />
            </div>
            
            <p className="text-2xl text-gray-300 mb-8">
              Kompletní realizace firemního letního kina
            </p>
            
            <div className="inline-flex items-center gap-8 bg-black/50 backdrop-blur-lg px-8 py-4 rounded-lg border border-[#0066B3]/30">
              <div className="text-center">
                <div className="text-[#0066B3] text-sm mb-1">DATUM</div>
                <div className="text-white font-bold text-xl">{offer.eventDate}</div>
              </div>
              <div className="w-px h-12 bg-[#0066B3]/30" />
              <div className="text-center">
                <div className="text-[#0066B3] text-sm mb-1">MÍSTO</div>
                <div className="text-white font-bold text-xl">{offer.eventLocation}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rest of the component will continue... */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        
        {/* Co zajišťujeme */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Co zajišťujeme
          </h2>
          <div className="w-24 h-1 bg-[#0066B3] mx-auto mb-12"></div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl p-6 hover:border-[#0066B3] transition-all">
              <div className="w-12 h-12 bg-[#0066B3]/20 rounded-lg flex items-center justify-center mb-4">
                <Film className="w-6 h-6 text-[#0066B3]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Technické zajištění</h3>
              <ul className="text-gray-400 space-y-2 text-sm">
                <li>• LED kino obrazovka 5×3m</li>
                <li>• Profesionální ozvučení</li>
                <li>• Konstrukce a montáž</li>
                <li>• Technici na místě</li>
              </ul>
            </div>
            
            <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl p-6 hover:border-[#0066B3] transition-all">
              <div className="w-12 h-12 bg-[#0066B3]/20 rounded-lg flex items-center justify-center mb-4">
                <Popcorn className="w-6 h-6 text-[#0066B3]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Catering</h3>
              <ul className="text-gray-400 space-y-2 text-sm">
                <li>• Popcorn a nachos</li>
                <li>• Hranolky</li>
                <li>• Točená zmrzlina</li>
                <li>• Nápoje a pivo</li>
              </ul>
            </div>
            
            <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl p-6 hover:border-[#0066B3] transition-all">
              <div className="w-12 h-12 bg-[#0066B3]/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#0066B3]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Kompletní servis</h3>
              <ul className="text-gray-400 space-y-2 text-sm">
                <li>• Kuchaři a barmani</li>
                <li>• Pomocný personál</li>
                <li>• Logistika a doprava</li>
                <li>• Food trucky na místě</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Menu vizualizace */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Letní kino menu
          </h2>
          <div className="w-24 h-1 bg-[#0066B3] mx-auto mb-12"></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Popcorn */}
            <div className="group relative bg-[#1a1f3a] rounded-xl overflow-hidden border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all duration-300 hover:scale-105">
              <div className="aspect-square bg-gradient-to-br from-[#0a0e27] to-[#1a1f3a] flex items-center justify-center p-6">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/c02bcbe3-02d5-4f93-a72a-1c7588058488/images/5fec83835bb3c83870338311c14da173e294511d18bea501016d5b7a05c4adf2.png"
                  alt="Popcorn"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-gradient-to-r from-[#0066B3] to-[#004080] py-4 text-center">
                <h3 className="text-2xl font-bold text-white uppercase tracking-wider">Popcorn</h3>
              </div>
            </div>

            {/* Nachos */}
            <div className="group relative bg-[#1a1f3a] rounded-xl overflow-hidden border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all duration-300 hover:scale-105">
              <div className="aspect-square bg-gradient-to-br from-[#0a0e27] to-[#1a1f3a] flex items-center justify-center p-6">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/c02bcbe3-02d5-4f93-a72a-1c7588058488/images/2e441749434c731308346f23a84f267cda78baf265f52428779a92dd4ffe7143.png"
                  alt="Nachos"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-gradient-to-r from-[#0066B3] to-[#004080] py-4 text-center">
                <h3 className="text-2xl font-bold text-white uppercase tracking-wider">Nachos</h3>
              </div>
            </div>

            {/* Hranolky */}
            <div className="group relative bg-[#1a1f3a] rounded-xl overflow-hidden border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all duration-300 hover:scale-105">
              <div className="aspect-square bg-gradient-to-br from-[#0a0e27] to-[#1a1f3a] flex items-center justify-center p-6">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/c02bcbe3-02d5-4f93-a72a-1c7588058488/images/616a7e3865f41eb639c6f61420d7995587f8eae7bf5fb1d045eb2281d3ed3aa7.png"
                  alt="Hranolky"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-gradient-to-r from-[#0066B3] to-[#004080] py-4 text-center">
                <h3 className="text-2xl font-bold text-white uppercase tracking-wider">Hranolky</h3>
              </div>
            </div>

            {/* Zmrzlina */}
            <div className="group relative bg-[#1a1f3a] rounded-xl overflow-hidden border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all duration-300 hover:scale-105">
              <div className="aspect-square bg-gradient-to-br from-[#0a0e27] to-[#1a1f3a] flex items-center justify-center p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/82cxqufp_Sni%CC%81mek%20obrazovky%202026-03-09%20v%C2%A010.29.13.png"
                  alt="Točená zmrzlina"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-gradient-to-r from-[#0066B3] to-[#004080] py-4 text-center">
                <h3 className="text-2xl font-bold text-white uppercase tracking-wider">Zmrzlina</h3>
              </div>
            </div>
          </div>
        </section>

        {/* Galerie LED obrazovky */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            LED kino obrazovka 5×3m
          </h2>
          <div className="w-24 h-1 bg-[#0066B3] mx-auto mb-6"></div>
          <p className="text-center text-gray-400 mb-12 max-w-3xl mx-auto">
            Profesionální LED obrazovka s konstrukcí a ozvučením pro váš firemní event
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Fotka 1 - Outdoor koncert večer */}
            <div className="relative group overflow-hidden rounded-xl cursor-pointer bg-[#1a1f3a] border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all">
              <img
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/x1ao7ufx_Sni%CC%81mek%20obrazovky%202026-03-11%20v%C2%A09.22.27.png"
                alt="LED obrazovka 5x3m - outdoor koncert večer"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
            </div>
            
            {/* Fotka 2 - Outdoor koncert den */}
            <div className="relative group overflow-hidden rounded-xl cursor-pointer bg-[#1a1f3a] border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all">
              <img
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/xs5czic7_Sni%CC%81mek%20obrazovky%202026-03-11%20v%C2%A09.22.20.png"
                alt="LED obrazovka 5x3m - outdoor koncert den"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
            </div>
          </div>
          
          {/* Specifikace */}
          <div className="mt-8 bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">Specifikace LED obrazovky</h3>
            <div className="grid md:grid-cols-3 gap-4 text-gray-300">
              <div>
                <div className="text-[#0066B3] font-bold mb-1">Rozměr</div>
                <div>5 × 3 metry (15 m²)</div>
              </div>
              <div>
                <div className="text-[#0066B3] font-bold mb-1">Konstrukce</div>
                <div>Hliníková konstrukce s traverzami</div>
              </div>
              <div>
                <div className="text-[#0066B3] font-bold mb-1">Ozvučení</div>
                <div>Profesionální sound systém</div>
              </div>
            </div>
          </div>
        </section>

        {/* Interaktivní kalkulace */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Interaktivní kalkulace
          </h2>
          <div className="w-24 h-1 bg-[#0066B3] mx-auto mb-12"></div>
          
          {/* Slider pro počet lidí */}
          <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Sliders className="w-6 h-6 text-[#0066B3]" />
                <h3 className="text-2xl font-bold text-white">Počet návštěvníků</h3>
              </div>
              <div className="text-4xl font-bold text-[#0066B3]">{guestCount}</div>
            </div>
            
            <input
              type="range"
              min="100"
              max="1000"
              step="50"
              value={guestCount}
              onChange={(e) => setGuestCount(Number(e.target.value))}
              className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
              style={{
                background: `linear-gradient(to right, #0066B3 0%, #0066B3 ${((guestCount - 100) / 900) * 100}%, #374151 ${((guestCount - 100) / 900) * 100}%, #374151 100%)`
              }}
            />
            
            <div className="flex justify-between mt-2 text-gray-500 text-sm">
              <span>100 osob</span>
              <span>1000 osob</span>
            </div>
          </div>

          {/* Tabulka - Soupis techniky */}
          <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl overflow-hidden mb-6">
            <div className="bg-[#0066B3]/20 px-6 py-4 border-b border-[#0066B3]/30">
              <h3 className="text-xl font-bold text-white">Soupis techniky</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#151933] text-gray-400 text-sm">
                    <th className="px-6 py-3 text-left">Položka</th>
                    <th className="px-6 py-3 text-center">Počet ks</th>
                    <th className="px-6 py-3 text-right">Cena / ks</th>
                    <th className="px-6 py-3 text-right">Cena celkem</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">LED obrazovka 5x3m</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right">30 000 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">30 000 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Konstrukce LED obrazovky</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right">8 000 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">8 000 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Ozvučení – kompletní realizace</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right">15 000 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">15 000 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10 bg-[#0066B3]/5">
                    <td className="px-6 py-4">Židle</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{chairs}</td>
                    <td className="px-6 py-4 text-right">50 Kč</td>
                    <td className="px-6 py-4 text-right font-bold text-[#0066B3]">{chairsPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t-2 border-[#0066B3] bg-[#0066B3]/10">
                    <td colSpan="3" className="px-6 py-4 text-right font-bold">CELKEM:</td>
                    <td className="px-6 py-4 text-right font-bold text-[#0066B3] text-lg">{technicalTotal.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabulka - Personál technika */}
          <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl overflow-hidden mb-6">
            <div className="bg-[#0066B3]/20 px-6 py-4 border-b border-[#0066B3]/30">
              <h3 className="text-xl font-bold text-white">Personál - technické zajištění</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#151933] text-gray-400 text-sm">
                    <th className="px-6 py-3 text-left">Název pracovní pozice</th>
                    <th className="px-6 py-3 text-center">Počet pracovníků</th>
                    <th className="px-6 py-3 text-right">Cena / hod</th>
                    <th className="px-6 py-3 text-center">Počet hodin</th>
                    <th className="px-6 py-3 text-right">Cena celkem</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Technik LED obrazovka</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right">500 Kč</td>
                    <td className="px-6 py-4 text-center">8</td>
                    <td className="px-6 py-4 text-right font-bold">4 000 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Zvukař</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right">400 Kč</td>
                    <td className="px-6 py-4 text-center">8</td>
                    <td className="px-6 py-4 text-right font-bold">3 200 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Montážní personál</td>
                    <td className="px-6 py-4 text-center">4</td>
                    <td className="px-6 py-4 text-right">200 Kč</td>
                    <td className="px-6 py-4 text-center">6</td>
                    <td className="px-6 py-4 text-right font-bold">4 800 Kč</td>
                  </tr>
                  <tr className="border-t-2 border-[#0066B3] bg-[#0066B3]/10">
                    <td colSpan="4" className="px-6 py-4 text-right font-bold">CELKEM:</td>
                    <td className="px-6 py-4 text-right font-bold text-[#0066B3] text-lg">{technicalPersonnelTotal.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabulka - Catering jídlo */}
          <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl overflow-hidden mb-6">
            <div className="bg-[#0066B3]/20 px-6 py-4 border-b border-[#0066B3]/30">
              <h3 className="text-xl font-bold text-white">Catering - jídlo</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#151933] text-gray-400 text-sm">
                    <th className="px-6 py-3 text-left">Název</th>
                    <th className="px-6 py-3 text-center">Gramáž/objem</th>
                    <th className="px-6 py-3 text-center">Počet porcí</th>
                    <th className="px-6 py-3 text-right">Cena / porce</th>
                    <th className="px-6 py-3 text-right">Cena celkem</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Popcorn</td>
                    <td className="px-6 py-4 text-center">3L</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{popcornPortions}</td>
                    <td className="px-6 py-4 text-right">144 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{popcornPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Nachos + 2 dipy</td>
                    <td className="px-6 py-4 text-center">150g</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{nachosPortions}</td>
                    <td className="px-6 py-4 text-right">144 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{nachosPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Hranolky + 2 dipy</td>
                    <td className="px-6 py-4 text-center">150g</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{hranolkyPortions}</td>
                    <td className="px-6 py-4 text-right">70 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{hranolkyPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Točená zmrzlina</td>
                    <td className="px-6 py-4 text-center">120g</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{zmrzlinaPortions}</td>
                    <td className="px-6 py-4 text-right">50 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{zmrzlinaPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t-2 border-[#0066B3] bg-[#0066B3]/10">
                    <td colSpan="4" className="px-6 py-4 text-right font-bold">CELKEM:</td>
                    <td className="px-6 py-4 text-right font-bold text-[#0066B3] text-lg">{cateringFoodTotal.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabulka - Catering nápoje */}
          <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl overflow-hidden mb-6">
            <div className="bg-[#0066B3]/20 px-6 py-4 border-b border-[#0066B3]/30">
              <h3 className="text-xl font-bold text-white">Catering - nápoje</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#151933] text-gray-400 text-sm">
                    <th className="px-6 py-3 text-left">Název</th>
                    <th className="px-6 py-3 text-center">Objem</th>
                    <th className="px-6 py-3 text-center">Počet porcí</th>
                    <th className="px-6 py-3 text-right">Cena / porce</th>
                    <th className="px-6 py-3 text-right">Cena celkem</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Radegast 10</td>
                    <td className="px-6 py-4 text-center">500 ml</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{radegastPortions}</td>
                    <td className="px-6 py-4 text-right">45 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{radegastPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Birell Pomelo & Grep</td>
                    <td className="px-6 py-4 text-center">500 ml</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{birellPortions}</td>
                    <td className="px-6 py-4 text-right">45 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{birellPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Točená limonáda</td>
                    <td className="px-6 py-4 text-center">500 ml</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{limonadaPortions}</td>
                    <td className="px-6 py-4 text-right">40 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{limonadaPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Voda PET</td>
                    <td className="px-6 py-4 text-center">500 ml</td>
                    <td className="px-6 py-4 text-center text-[#0066B3] font-bold">{vodaPortions}</td>
                    <td className="px-6 py-4 text-right">35 Kč</td>
                    <td className="px-6 py-4 text-right font-bold">{vodaPrice.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                  <tr className="border-t-2 border-[#0066B3] bg-[#0066B3]/10">
                    <td colSpan="4" className="px-6 py-4 text-right font-bold">CELKEM:</td>
                    <td className="px-6 py-4 text-right font-bold text-[#0066B3] text-lg">{beveragesTotal.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabulka - Personál catering */}
          <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl overflow-hidden mb-6">
            <div className="bg-[#0066B3]/20 px-6 py-4 border-b border-[#0066B3]/30">
              <h3 className="text-xl font-bold text-white">Personál - catering</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#151933] text-gray-400 text-sm">
                    <th className="px-6 py-3 text-left">Název pracovní pozice</th>
                    <th className="px-6 py-3 text-center">Počet pracovníků</th>
                    <th className="px-6 py-3 text-right">Cena / hod</th>
                    <th className="px-6 py-3 text-center">Počet hodin</th>
                    <th className="px-6 py-3 text-right">Cena celkem</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Kuchař</td>
                    <td className="px-6 py-4 text-center">2</td>
                    <td className="px-6 py-4 text-right">350 Kč</td>
                    <td className="px-6 py-4 text-center">5</td>
                    <td className="px-6 py-4 text-right font-bold">3 500 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Barman</td>
                    <td className="px-6 py-4 text-center">2</td>
                    <td className="px-6 py-4 text-right">350 Kč</td>
                    <td className="px-6 py-4 text-center">5</td>
                    <td className="px-6 py-4 text-right font-bold">3 500 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Zmrzlinář</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right">200 Kč</td>
                    <td className="px-6 py-4 text-center">5</td>
                    <td className="px-6 py-4 text-right font-bold">1 000 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Pomocný personál</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right">200 Kč</td>
                    <td className="px-6 py-4 text-center">5</td>
                    <td className="px-6 py-4 text-right font-bold">1 000 Kč</td>
                  </tr>
                  <tr className="border-t-2 border-[#0066B3] bg-[#0066B3]/10">
                    <td colSpan="4" className="px-6 py-4 text-right font-bold">CELKEM:</td>
                    <td className="px-6 py-4 text-right font-bold text-[#0066B3] text-lg">{cateringPersonnelTotal.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabulka - Cateringová technika */}
          <div className="bg-[#1a1f3a] border border-[#0066B3]/30 rounded-xl overflow-hidden mb-6">
            <div className="bg-[#0066B3]/20 px-6 py-4 border-b border-[#0066B3]/30">
              <h3 className="text-xl font-bold text-white">Cateringová technika</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#151933] text-gray-400 text-sm">
                    <th className="px-6 py-3 text-left">Položka</th>
                    <th className="px-6 py-3 text-center">Počet ks</th>
                    <th className="px-6 py-3 text-right">Cena celkem</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Beer Truck XXL</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right font-bold">5 000 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Sweet Truck – zmrzlina</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right font-bold">5 000 Kč</td>
                  </tr>
                  <tr className="border-t border-[#0066B3]/10">
                    <td className="px-6 py-4">Cooking stan 6x3m</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-right font-bold">3 500 Kč</td>
                  </tr>
                  <tr className="border-t-2 border-[#0066B3] bg-[#0066B3]/10">
                    <td colSpan="2" className="px-6 py-4 text-right font-bold">CELKEM:</td>
                    <td className="px-6 py-4 text-right font-bold text-[#0066B3] text-lg">{cateringEquipmentTotal.toLocaleString('cs-CZ')} Kč</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </section>

        {/* Galerie - Food Trucky */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Naše mobilní catering zařízení
          </h2>
          <div className="w-24 h-1 bg-[#0066B3] mx-auto mb-12"></div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Beer Truck XXL */}
            <div className="relative group overflow-hidden rounded-xl cursor-pointer bg-[#1a1f3a] border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all">
              <img
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/yhkyp9il_vycepni-vuz-L-1-scaled%282%29.jpg"
                alt="Beer Truck XXL - Všeproakci Catering"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <h3 className="text-white font-bold text-xl">Beer Truck XXL</h3>
                <p className="text-gray-300 text-sm">Točené pivo a nápoje</p>
              </div>
            </div>
            
            {/* Sweet Truck */}
            <div className="relative group overflow-hidden rounded-xl cursor-pointer bg-[#1a1f3a] border-2 border-[#0066B3]/30 hover:border-[#0066B3] transition-all">
              <img
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/s2k3gb7p_Snimek-obrazovky-2025-01-12-v-16.49.06%281%29.png"
                alt="Sweet Truck - Točená zmrzlina"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <h3 className="text-white font-bold text-xl">Sweet Truck</h3>
                <p className="text-gray-300 text-sm">Točená zmrzlina</p>
              </div>
            </div>
          </div>
        </section>

        {/* Souhrn - Celková cena */}
        <section className="mb-20">
          <div className="bg-gradient-to-br from-[#0066B3] to-[#004080] rounded-2xl p-8 shadow-2xl border-2 border-[#0066B3]">
            <h2 className="text-3xl font-bold text-center text-white mb-8">
              Celková nabídková cena
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <div className="text-white/80 text-sm mb-2">Počet návštěvníků</div>
                <div className="text-4xl font-bold text-white">{guestCount} osob</div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <div className="text-white/80 text-sm mb-2">Cena na osobu</div>
                <div className="text-4xl font-bold text-white">{pricePerPerson} Kč</div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-lg rounded-xl p-8 border border-white/30 mb-8">
              <div className="text-white/90 text-lg mb-3">Celková cena za akci</div>
              <div className="text-6xl font-bold text-white">
                {totalPrice.toLocaleString('cs-CZ')} Kč
              </div>
            </div>

            {/* PDF Export Button */}
            <div className="flex justify-center">
              <button
                onClick={exportTablesOnly}
                className="flex items-center justify-center gap-3 bg-white text-[#0066B3] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all shadow-lg text-lg"
              >
                <Download className="w-6 h-6" />
                Stáhnout kalkulaci (PDF)
              </button>
            </div>
          </div>
        </section>

        {/* Kontaktujte nás - sekce */}
        <section className="py-20 bg-gradient-to-b from-[#0a0e27] to-[#0f1535]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Kontaktujte nás
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Rádi zodpovíme vaše dotazy a upřesníme nabídku
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-3xl shadow-2xl overflow-hidden">
              <div className="p-8 md:p-12 text-white">
                <div className="grid md:grid-cols-2 gap-12">
                  {/* Company Info */}
                  <div>
                    <img
                      src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/xfkrm6jm_logo-white.png"
                      alt="Vše pro akci"
                      className="h-12 mb-6"
                    />
                    <h3 className="text-2xl font-bold mb-6">
                      Vše pro akci s.r.o.
                    </h3>
                    <p className="text-cyan-100 mb-8 leading-relaxed">
                      Specializujeme se na profesionální cateringové služby pro firemní akce, 
                      svatby a velké eventy. S více než 15 lety zkušeností jsme připravili 
                      stovky úspěšných akcí pro spokojené zákazníky.
                    </p>
                    <a
                      href="https://vseproakci.cz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-6 py-3 rounded-xl transition-all duration-300 font-medium"
                    >
                      <span>Navštivte náš web</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  
                  {/* Contact Person */}
                  <div>
                    <h3 className="text-2xl font-bold mb-6">Kontaktní osoba</h3>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 mb-6">
                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/bvn0z37e_Sni%CC%81mek%20obrazovky%202026-03-11%20v%C2%A08.53.35.png"
                          alt="Jakub Vodárek"
                          className="rounded-full w-16 h-16 object-cover border-2 border-white/30"
                        />
                        <div>
                          <div className="text-xl font-bold">
                            Jakub Vodárek
                          </div>
                          <div className="text-cyan-100">
                            Jednatel
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <a
                        href="tel:774750456"
                        className="flex items-center space-x-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-4 transition-all duration-300 border border-white/20"
                      >
                        <div className="bg-white/20 rounded-lg p-3">
                          <Phone className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-sm text-cyan-100">Telefon</div>
                          <div className="font-bold text-lg">774 750 456</div>
                        </div>
                      </a>
                      
                      <a
                        href="mailto:info@vseproakci.cz"
                        className="flex items-center space-x-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-4 transition-all duration-300 border border-white/20"
                      >
                        <div className="bg-white/20 rounded-lg p-3">
                          <Mail className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-sm text-cyan-100">Email</div>
                          <div className="font-bold text-lg">info@vseproakci.cz</div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Client References */}
            <div className="mt-16 text-center">
              <h3 className="text-2xl font-bold text-white mb-8">
                Důvěřují nám
              </h3>
              <div className="flex flex-wrap items-center justify-center gap-12">
                <div className="text-gray-400 font-semibold text-lg">Fatra a.s.</div>
                <div className="text-gray-400 font-semibold text-lg">Česká televize</div>
                <div className="text-gray-400 font-semibold text-lg">Amazon</div>
                <div className="text-gray-400 font-semibold text-lg">Barum Continental</div>
                <div className="text-gray-400 font-semibold text-lg">Plzeňský Prazdroj</div>
                <div className="text-gray-400 font-semibold text-lg">KBP</div>
                <div className="text-gray-400 font-semibold text-lg">Greiner Packaging</div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gradient-to-br from-gray-900 to-slate-900 text-white py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-12 mb-8">
              {/* Company Info */}
              <div>
                <img
                  src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/xfkrm6jm_logo-white.png"
                  alt="Vše pro akci"
                  className="h-10 mb-4"
                />
                <p className="text-gray-400 text-sm leading-relaxed">
                  Profesionální cateringové služby pro vaše nezapomenutelné akce.
                </p>
              </div>
              
              {/* Contact */}
              <div>
                <h4 className="text-lg font-bold mb-4">Kontakt</h4>
                <div className="space-y-3 text-sm">
                  <a
                    href="tel:774750456"
                    className="flex items-center space-x-2 text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>774 750 456</span>
                  </a>
                  <a
                    href="mailto:info@vseproakci.cz"
                    className="flex items-center space-x-2 text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span>info@vseproakci.cz</span>
                  </a>
                </div>
              </div>
              
              {/* Event Info */}
              <div>
                <h4 className="text-lg font-bold mb-4">Akce</h4>
                <div className="text-sm text-gray-400 space-y-2">
                  <p className="font-semibold text-white">{offer.eventName}</p>
                  <p>{offer.clientName}</p>
                  <p>{offer.eventDate}</p>
                  <p className="text-xs">{offer.eventLocation}</p>
                </div>
              </div>
            </div>
            
            {/* Additional Info */}
            <div className="border-t border-gray-800 pt-8 mb-6">
              <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-lg p-6 text-center border border-cyan-800/30">
                <h4 className="text-lg font-bold mb-3 text-cyan-300">📦 Kompletní inventář a mobiliář</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Disponujeme kompletním inventářem a mobiliářem pro realizaci vaší akce. 
                  V případě zájmu nabízíme pronájem nábytku, stanů, osvětlení a dalšího vybavení.
                </p>
                <a
                  href="https://vseproakci.cz/sluzby/pronajem-vybaveni/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors font-medium text-sm"
                >
                  Zobrazit kompletní nabídku pronájmu →
                </a>
              </div>
            </div>
            
            <div className="border-t border-gray-800 pt-8 text-center">
              <p className="text-gray-500 text-sm">
                © 2026 Vše pro akci s.r.o. Vytvořeno pro {offer.clientName}
              </p>
              <p className="text-gray-600 text-xs mt-2">
                Tato nabídka je platná do 11. 4. 2026
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default FatraOfferPage;
